/**
 * DailyCheckinNudge — A2 (Feb 27, 2026 exposure batch).
 *
 * Doctrine: learner exposure is contextual + progressive + calm.
 * This component surfaces the existing /api/checkin/today flow as a
 * single quiet line on the learner home, ONLY when:
 *
 *   1. The user has NOT already checked in today.
 *   2. The user has NOT dismissed it this session.
 *
 * It is never sticky. It surfaces one meaningful next step and gets
 * out of the way. Mirrors the pattern documented in
 * /app/memory/OPERATIONAL_EXPOSURE_MAPPING_V1.md §2.A2.
 *
 * Never:
 *   - rendered for non-learners
 *   - rendered without a flag-guarded fetch
 *   - given operational/diagnostic weight
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_DISMISS_KEY = "daily_checkin_nudge_dismissed";


export default function DailyCheckinNudge() {
  const [shouldShow, setShouldShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/checkin/today`, {
          withCredentials: true,
        });
        if (!alive) return;
        // Only nudge when today's check-in hasn't been completed yet.
        if (r.data?.completed_today === false) {
          setShouldShow(true);
        }
      } catch {
        // Calmly silent — never surface a network error on a dashboard nudge.
      }
    })();
    return () => { alive = false; };
  }, [dismissed]);

  const onDismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* silent */ }
    setDismissed(true);
    setShouldShow(false);
  };

  if (!shouldShow || dismissed) return null;

  return (
    <div
      data-testid="daily-checkin-nudge"
      className="flex items-center gap-3 rounded-2xl border border-slate-700/60 bg-slate-900/40 px-4 py-3"
    >
      <Sparkles className="w-4 h-4 text-amber-300 flex-shrink-0" aria-hidden />
      <Link
        to="/daily-checkin"
        data-testid="daily-checkin-nudge-link"
        className="flex-1 text-[13.5px] text-slate-100 hover:text-white transition-colors"
      >
        How are you feeling about driving today?
      </Link>
      <button
        type="button"
        data-testid="daily-checkin-nudge-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-slate-500 hover:text-slate-300 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
